import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { LibraryCardPrintSheet } from "@/components/LibraryCardPrintSheet";
import { libraryCardsApi, type LibraryCard } from "@/lib/api/library-cards";
import { registrationApi } from "@/lib/api/registration";
import api from "@/lib/api";
import {
  loadRegistrationFieldsByPatronType,
  type CardRegistrationField,
} from "@/lib/libraryCardFields";
import { LIBRARY_CARD_PDF_CAPTURE_SCALE, libraryCardPdfImageRect } from "@/lib/libraryCardPdf";
import {
  mergeLibraryCardPrintSettings,
  type LibraryCardPrintSettings,
} from "@/lib/libraryCardPrintConfig";
import { Printer, Loader2, ArrowLeft, FileDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

type CardWithToken = LibraryCard & { verificationToken: string };

function safePdfFilenameSegment(name: string): string {
  return (
    name
      .replace(/[^\w\-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "library"
  );
}

export default function LibraryCardPrintPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const toast = useToast();
  const printSheetRef = useRef<HTMLDivElement>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const cardIdsParam = searchParams.get("cardIds");
  const cardIds = cardIdsParam ? cardIdsParam.split(",").filter(Boolean) : undefined;

  const [cards, setCards] = useState<CardWithToken[]>([]);
  const [stampUrl, setStampUrl] = useState<string | undefined>();
  const [signatureUrl, setSignatureUrl] = useState<string | undefined>();
  const [brandPrimaryColor, setBrandPrimaryColor] = useState("#2563EB");
  const [libraryFullName, setLibraryFullName] = useState<string | undefined>();
  const [libraryAddress, setLibraryAddress] = useState<string | undefined>();
  const [printConfig, setPrintConfig] = useState<LibraryCardPrintSettings>(() =>
    mergeLibraryCardPrintSettings(undefined)
  );
  const [fieldsByPatronType, setFieldsByPatronType] = useState<
    Record<string, CardRegistrationField[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const hasPrintPermission =
    user?.permissions?.some(
      (p: string) => p === "cards.print" || p === "cards.process" || p === "*"
    ) ?? false;

  const { isTenantAdmin } = useAccessControl();
  const canPrint = isTenantAdmin || hasPrintPermission;

  useEffect(() => {
    if (!canPrint) return;
    loadData();
  }, [canPrint, cardIdsParam, tenant?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      type CardAssets = { stampUrl?: string; signatureUrl?: string };
      type BrandingResp = {
        primaryColor?: string;
        libraryFullName?: string | null;
        libraryAddress?: string | null;
      };
      const [printPayload, assetsData, brandingData] = await Promise.all([
        libraryCardsApi.getPrintData(cardIds),
        tenant?.id
          ? api
              .get<CardAssets>(`/tenants/${tenant.id}/card-assets`)
              .then((r) => r.data)
              .catch(() => ({}) as CardAssets)
          : Promise.resolve({} as CardAssets),
        tenant?.id
          ? api
              .get<BrandingResp>(`/tenants/${tenant.id}/branding`)
              .then((r) => r.data)
              .catch(() => ({}) as BrandingResp)
          : Promise.resolve({} as BrandingResp),
      ]);
      setCards(printPayload.cards);
      setStampUrl(assetsData.stampUrl);
      setSignatureUrl(assetsData.signatureUrl);
      setBrandPrimaryColor(brandingData.primaryColor || "#2563EB");
      setLibraryFullName(brandingData.libraryFullName?.trim() || undefined);
      setLibraryAddress(brandingData.libraryAddress?.trim() || undefined);
      setPrintConfig(
        mergeLibraryCardPrintSettings(
          printPayload.libraryCardPrint ??
            (tenant?.settings as Record<string, unknown> | undefined)?.libraryCardPrint
        )
      );
      const patronTypeIds = printPayload.cards
        .map((card) => card.membership?.patronType?.id)
        .filter((id): id is string => Boolean(id));
      const fieldCatalog = await loadRegistrationFieldsByPatronType(patronTypeIds, (patronTypeId) =>
        registrationApi.getAdminRegistrationConfig(patronTypeId)
      );
      setFieldsByPatronType(fieldCatalog);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load card data");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSavePdf = useCallback(async () => {
    const el = printSheetRef.current;
    if (!el) return;
    setPdfBusy(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const pageNodes = Array.from(el.querySelectorAll<HTMLElement>(".library-card-a4-page"));
      const targets = pageNodes.length > 0 ? pageNodes : [el];
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < targets.length; i += 1) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(targets[i], {
          scale: LIBRARY_CARD_PDF_CAPTURE_SCALE,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
        });
        const rect = libraryCardPdfImageRect({
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          captureScale: LIBRARY_CARD_PDF_CAPTURE_SCALE,
          pageWidthMm: pageW,
          pageHeightMm: pageH,
        });
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", rect.x, rect.y, rect.width, rect.height);
      }
      const name = safePdfFilenameSegment(libraryFullName || tenant?.name || "library-cards");
      pdf.save(`${name}-cards.pdf`);
    } catch (e) {
      console.error("PDF export failed", e);
      toast.error("Could not create PDF. Try Print and save as PDF instead.");
      window.print();
    } finally {
      setPdfBusy(false);
    }
  }, [libraryFullName, tenant?.name, toast]);

  if (!canPrint) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p className="text-gray-600">
              You need permission to print library cards. Contact your administrator.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate("/admin/cards")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Cards
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (cards.length === 0) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 mb-4">No cards selected for printing.</p>
            <Button onClick={() => navigate("/admin/cards")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Cards
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="py-4 px-4 max-w-[210mm] mx-auto print:p-0 print:max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 print:hidden">
          <Button variant="ghost" onClick={() => navigate("/admin/cards")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Cards
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={pdfBusy}
              onClick={() => void handleSavePdf()}
            >
              {pdfBusy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              Save as PDF
            </Button>
            <Button type="button" onClick={handlePrint} disabled={pdfBusy}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
        <LibraryCardPrintSheet
          ref={printSheetRef}
          cards={cards}
          stampUrl={stampUrl}
          signatureUrl={signatureUrl}
          printConfig={printConfig}
          brandPrimaryColor={brandPrimaryColor}
          libraryFullName={libraryFullName}
          libraryAddress={libraryAddress}
          fieldsByPatronType={fieldsByPatronType}
        />
      </div>
    </AppLayout>
  );
}
